import { useEffect, useMemo, useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ChevronLeft, ChevronRight, FileDown, FileText, RefreshCcw, Briefcase, Plane, Stethoscope, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import DriverDetailModal from "@/components/DriverDetailModal";
import WorktimeExportDialog from "@/components/WorktimeExportDialog";

const WEEKDAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const mondayOf = (d) => {
  const x = new Date(d);
  const wd = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - wd);
  x.setHours(0, 0, 0, 0);
  return x;
};
const firstOfMonth = (d) => { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; };
const toKey = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const fmtRange = (a, b) => {
  const f = (s) => `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}`;
  return a && b ? `${f(a)} – ${f(b)}` : "";
};
const fmtMonth = (iso) => iso ? new Date(iso + "T00:00:00").toLocaleDateString("de-DE", { month: "long", year: "numeric" }) : "";
const fmtHM = (mins) => `${Math.floor((mins || 0) / 60)}h ${(mins || 0) % 60}m`;
const dayMeta = (iso) => {
  const dt = new Date(iso + "T00:00:00");
  const dow = dt.getDay();
  return { wd: WEEKDAYS_DE[(dow + 6) % 7], num: iso.slice(8, 10), mon: iso.slice(5, 7), weekend: dow === 0 || dow === 6 };
};

const STATUS = {
  arbeit: { label: "Arbeit", box: "bg-blue-500 text-white border-blue-600", abbr: "A" },
  urlaub: { label: "Urlaub", box: "bg-sky-400 text-white border-sky-500", abbr: "U" },
  krank: { label: "Krank", box: "bg-orange-400 text-white border-orange-500", abbr: "K" },
  "": { label: "Frei", box: "bg-muted text-muted-foreground/40 border-border", abbr: "" },
};

export default function WorkTimeOverview() {
  const [mode, setMode] = useState("week"); // "week" | "month"
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState({ days: [], drivers: [], period_start: "", period_end: "" });
  const [drivers, setDrivers] = useState([]);
  const [detailDriver, setDetailDriver] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportKind, setExportKind] = useState(null); // "csv" | "pdf" | null

  const periodKey = mode === "week" ? toKey(mondayOf(cursor)) : toKey(firstOfMonth(cursor));

  const load = useCallback(async (showSpin = true) => {
    if (showSpin) setLoading(true);
    try {
      const key = mode === "week" ? toKey(mondayOf(cursor)) : toKey(firstOfMonth(cursor));
      const url = mode === "week" ? "/worktimes/overview" : "/worktimes/overview-month";
      const params = mode === "week" ? { week_start: key } : { month_start: key };
      const [ov, drv] = await Promise.all([
        api.get(url, { params }),
        api.get("/drivers"),
      ]);
      const d = ov.data;
      setData({
        days: d.days,
        drivers: d.drivers,
        period_start: d.week_start || d.month_start,
        period_end: d.week_end || d.month_end,
      });
      setDrivers(drv.data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error(formatApiError(e));
    } finally {
      if (showSpin) setLoading(false);
    }
  }, [mode, cursor]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => load(false), 20000);
    return () => clearInterval(id);
  }, [load]);

  const shift = (delta) => setCursor((c) => {
    const x = new Date(c);
    if (mode === "week") x.setDate(x.getDate() + delta * 7);
    else x.setMonth(x.getMonth() + delta);
    return x;
  });

  const openDriver = (driverId) => {
    const d = drivers.find((x) => x.id === driverId);
    if (d) setDetailDriver(d);
  };

  const doExport = (kind) => setExportKind(kind);

  const totals = useMemo(() => {
    const t = { arbeit: 0, urlaub: 0, krank: 0, minutes: 0 };
    for (const d of data.drivers) { t.arbeit += d.arbeit_days; t.urlaub += d.urlaub_days; t.krank += d.krank_days; t.minutes += d.work_minutes; }
    return t;
  }, [data]);

  const isMonth = mode === "month";
  const cellBox = isMonth ? "h-9 w-9 text-[11px]" : "h-11 w-16 text-xs";
  const colMin = isMonth ? "min-w-[40px]" : "min-w-[84px]";

  return (
    <div className="space-y-4" data-testid="worktime-overview-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl heading font-semibold">Arbeitszeitübersicht</h1>
          <p className="text-sm text-muted-foreground">Übersicht je Fahrer: Arbeit, Urlaub und Krank – live aktualisiert. Wochen- oder Monatsansicht.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList data-testid="overview-mode-tabs">
              <TabsTrigger value="week" data-testid="overview-mode-week">Woche</TabsTrigger>
              <TabsTrigger value="month" data-testid="overview-mode-month">Monat</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button onClick={() => load()} variant="ghost" size="icon" aria-label="Aktualisieren" data-testid="overview-refresh">
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="overview-export-button"><FileDown className="h-4 w-4 mr-1" /> {isMonth ? "Monat" : "Woche"} exportieren</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => doExport("csv")} data-testid="overview-export-csv"><FileDown className="h-4 w-4 mr-2" /> CSV herunterladen</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => doExport("pdf")} data-testid="overview-export-pdf"><FileText className="h-4 w-4 mr-2" /> PDF herunterladen</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Period navigation + legend */}
      <Card className="p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} aria-label="Zurück" data-testid="overview-prev-period"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/60 text-sm font-medium tabular-nums min-w-[200px] justify-center" data-testid="overview-period-range">
            <CalendarRange className="h-4 w-4 text-muted-foreground" />
            {isMonth ? fmtMonth(data.period_start) : fmtRange(data.period_start, data.period_end)}
          </div>
          <Button variant="outline" size="icon" onClick={() => shift(1)} aria-label="Vor" data-testid="overview-next-period"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())} data-testid="overview-this-period">{isMonth ? "Aktueller Monat" : "Aktuelle Woche"}</Button>
        </div>
        <div className="ml-auto flex items-center gap-4 text-xs">
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-blue-500" /> Arbeit</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-sky-400" /> Urlaub</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-orange-400" /> Krank</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-muted border border-border" /> Frei</span>
        </div>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-3"><div className="text-xs uppercase text-muted-foreground tracking-wider flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> Arbeitstage</div><div className="mt-1 text-2xl heading font-semibold tabular-nums" data-testid="overview-total-arbeit">{totals.arbeit}</div></Card>
        <Card className="p-3"><div className="text-xs uppercase text-muted-foreground tracking-wider flex items-center gap-1"><Plane className="h-3.5 w-3.5" /> Urlaubstage</div><div className="mt-1 text-2xl heading font-semibold tabular-nums" data-testid="overview-total-urlaub">{totals.urlaub}</div></Card>
        <Card className="p-3"><div className="text-xs uppercase text-muted-foreground tracking-wider flex items-center gap-1"><Stethoscope className="h-3.5 w-3.5" /> Kranktage</div><div className="mt-1 text-2xl heading font-semibold tabular-nums" data-testid="overview-total-krank">{totals.krank}</div></Card>
        <Card className="p-3"><div className="text-xs uppercase text-muted-foreground tracking-wider">Gesamt Arbeitszeit</div><div className="mt-1 text-2xl heading font-semibold tabular-nums" data-testid="overview-total-minutes">{fmtHM(totals.minutes)}</div></Card>
      </div>

      {/* Grid */}
      <Card className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3 sticky left-0 bg-muted/60 z-10 min-w-[170px]">Fahrer</th>
              {data.days.map((d) => {
                const m = dayMeta(d);
                return (
                  <th key={d} className={`text-center px-1 py-2 ${colMin} ${m.weekend ? "bg-muted" : ""}`}>
                    <div className="leading-tight">
                      <div className="text-[10px] font-normal">{m.wd}</div>
                      <div className="tabular-nums">{m.num}{isMonth ? "" : `.${m.mon}`}</div>
                    </div>
                  </th>
                );
              })}
              <th className="text-center px-3 py-3 min-w-[90px]">Arbeitszeit</th>
            </tr>
          </thead>
          <tbody>
            {data.drivers.map((row) => (
              <tr key={row.driver_id} className="border-t border-border row-hover" data-testid={`overview-row-${row.driver_id}`}>
                <td className="px-4 py-2 sticky left-0 bg-card z-10">
                  <button onClick={() => openDriver(row.driver_id)} className="text-blue-700 dark:text-blue-300 hover:underline font-medium text-left" data-testid={`overview-driver-link-${row.driver_id}`}>
                    {row.driver_name}
                  </button>
                </td>
                {row.cells.map((c) => {
                  const s = STATUS[c.status] || STATUS[""];
                  return (
                    <td key={c.date} className="px-1 py-1.5 text-center" data-testid={`overview-cell-${row.driver_id}-${c.date}`}>
                      <div
                        className={`mx-auto flex flex-col items-center justify-center rounded-md border font-semibold transition-colors ${cellBox} ${s.box}`}
                        title={`${row.driver_name} · ${c.date} · ${s.label}${c.status === "arbeit" ? ` · ${fmtHM(c.work_minutes)}` : ""}`}
                      >
                        <span>{s.abbr}</span>
                        {!isMonth && c.status === "arbeit" && c.work_minutes > 0 && (
                          <span className="text-[10px] font-normal opacity-90 tabular-nums">{fmtHM(c.work_minutes)}</span>
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-center tabular-nums font-semibold" data-testid={`overview-driver-minutes-${row.driver_id}`}>{fmtHM(row.work_minutes)}</td>
              </tr>
            ))}
            {data.drivers.length === 0 && (
              <tr><td colSpan={data.days.length + 2} className="px-4 py-8 text-center text-muted-foreground text-sm">Keine aktiven Fahrer.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <DriverDetailModal driver={detailDriver} open={!!detailDriver} onOpenChange={(o) => !o && setDetailDriver(null)} />
      <WorktimeExportDialog open={!!exportKind} onOpenChange={(o) => !o && setExportKind(null)} kind={exportKind || "csv"} drivers={drivers} periodStart={data.period_start} periodEnd={data.period_end} />
    </div>
  );
}
